import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../api/client';
import { Shield, Loader2 } from 'lucide-react';

const SITE_FLAGS: Record<string, string> = { MLA: '🇦🇷', MLM: '🇲🇽', MLC: '🇨🇱', MCO: '🇨🇴', MLU: '🇺🇾', MLB: '🇧🇷', MLV: '🇻🇪', MPE: '🇵🇪', MEC: '🇪🇨' };

const LEVEL_COLORS: Record<string, string> = {
  '5_green': 'text-green-400',
  '4_light_green': 'text-green-300',
  '3_yellow': 'text-yellow-400',
  '2_orange': 'text-orange-400',
  '1_red': 'text-red-400',
};

const LEVEL_LABELS: Record<string, string> = {
  '5_green': 'Green',
  '4_light_green': 'Light Green',
  '3_yellow': 'Yellow',
  '2_orange': 'Orange',
  '1_red': 'Red',
};

const POWER_LABELS: Record<string, string> = {
  gold: '🥇 Gold',
  silver: '🥈 Silver',
  platinum: '🏆 Platinum',
};

interface SiteReputation {
  user_id: number;
  site_id: string;
  logistic_type: string;
  seller_reputation: {
    level_id: string | null;
    power_seller_status: string | null;
    transactions: {
      canceled: number;
      completed: number;
      period: string;
      ratings: { negative: number; neutral: number; positive: number };
      total: number;
    };
    metrics: {
      sales: { period: string; completed: number };
      claims: { period: string; rate: number; value: number };
      delayed_handling_time: { period: string; rate: number; value: number };
      cancellations: { period: string; rate: number; value: number };
    };
  };
}

interface ListingCap {
  user_id: number;
  site_id: string;
  quota: number;
  total_items: number;
}

export default function ReputationPanel({ sellerId }: { sellerId: number }) {
  const { t } = useTranslation();
  const [reputations, setReputations] = useState<SiteReputation[]>([]);
  const [caps, setCaps] = useState<ListingCap[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const [repRes, capRes] = await Promise.all([
        api.get<any>(`/mercadolibre/metrics-live/reputation?seller_id=${sellerId}`),
        api.get<ListingCap[]>(`/mercadolibre/metrics-live/caps?seller_id=${sellerId}`),
      ]);
      if (repRes.success && repRes.data) {
        setReputations(repRes.data.seller_reputation || []);
      }
      if (capRes.success && capRes.data) {
        setCaps(capRes.data);
      }
      setLoading(false);
    })();
  }, [sellerId]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-[var(--color-text-muted)] p-4">
        <Loader2 className="w-4 h-4 animate-spin" /> {t('common.loading')}
      </div>
    );
  }

  if (reputations.length === 0) return null;

  return (
    <div>
      <h3 className="text-sm font-medium text-[var(--color-text)] mb-3 flex items-center gap-2">
        <Shield className="w-4 h-4" /> {t('ml.rep.title')}
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {reputations.map((rep) => {
          const r = rep.seller_reputation;
          const cap = caps.find(c => c.site_id === rep.site_id);
          const levelColor = LEVEL_COLORS[r.level_id || ''] || 'text-[var(--color-text-muted)]';
          const levelLabel = LEVEL_LABELS[r.level_id || ''] || (r.level_id || 'N/A');
          const powerLabel = POWER_LABELS[r.power_seller_status || ''] || null;

          return (
            <div
              key={rep.site_id}
              className="bg-[var(--color-bg-secondary)] rounded-lg border border-[var(--color-border)] p-4"
            >
              {/* Header */}
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className="text-xl">{SITE_FLAGS[rep.site_id] || '🌍'}</span>
                  <span className="text-sm font-bold text-[var(--color-text)]">{rep.site_id}</span>
                </div>
                <div className="text-right">
                  <div className={`text-sm font-bold ${levelColor}`}>{levelLabel}</div>
                  {powerLabel && (
                    <div className="text-xs text-[var(--color-text-muted)]">{powerLabel}</div>
                  )}
                </div>
              </div>

              {/* Transactions */}
              <div className="grid grid-cols-3 gap-2 text-center mb-3">
                <div>
                  <div className="text-xs text-[var(--color-text-muted)]">{t('ml.rep.completed')}</div>
                  <div className="text-sm font-bold text-green-400">{r.transactions.completed}</div>
                </div>
                <div>
                  <div className="text-xs text-[var(--color-text-muted)]">{t('ml.rep.canceled')}</div>
                  <div className="text-sm font-bold text-red-400">{r.transactions.canceled}</div>
                </div>
                <div>
                  <div className="text-xs text-[var(--color-text-muted)]">{t('ml.rep.positive')}</div>
                  <div className="text-sm font-bold text-green-400">{(r.transactions.ratings.positive * 100).toFixed(0)}%</div>
                </div>
              </div>

              {/* Metrics bars */}
              <div className="space-y-2">
                <MetricBar
                  label={t('ml.rep.claims')}
                  rate={r.metrics.claims.rate}
                  value={r.metrics.claims.value}
                  thresholds={[0.02, 0.04, 0.07]}
                />
                <MetricBar
                  label={t('ml.rep.handling')}
                  rate={r.metrics.delayed_handling_time.rate}
                  value={r.metrics.delayed_handling_time.value}
                  thresholds={[0.15, 0.20, 0.30]}
                />
                <MetricBar
                  label={t('ml.rep.cancellations')}
                  rate={r.metrics.cancellations.rate}
                  value={r.metrics.cancellations.value}
                  thresholds={[0.03, 0.04, 0.05]}
                />
              </div>

              {/* Listing cap */}
              {cap && (
                <div className="mt-3 pt-2 border-t border-[var(--color-border)] flex justify-between text-xs">
                  <span className="text-[var(--color-text-muted)]">{t('ml.rep.listingCap')}</span>
                  <span className="text-[var(--color-text)]">
                    {cap.total_items} / {cap.quota.toLocaleString()}
                  </span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function MetricBar({ label, rate, value, thresholds }: {
  label: string;
  rate: number;
  value: number;
  thresholds: [number, number, number]; // green/yellow/orange boundaries
}) {
  const pct = (rate * 100).toFixed(1);
  const color = rate <= thresholds[0] ? 'bg-green-500' :
                rate <= thresholds[1] ? 'bg-yellow-500' :
                rate <= thresholds[2] ? 'bg-orange-500' :
                'bg-red-500';
  const textColor = rate <= thresholds[0] ? 'text-green-400' :
                    rate <= thresholds[1] ? 'text-yellow-400' :
                    rate <= thresholds[2] ? 'text-orange-400' :
                    'text-red-400';

  return (
    <div>
      <div className="flex justify-between text-xs mb-0.5">
        <span className="text-[var(--color-text-muted)]">{label}</span>
        <span className={textColor}>{pct}% ({value})</span>
      </div>
      <div className="h-1.5 bg-[var(--color-bg)] rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full ${color} transition-all`}
          style={{ width: `${Math.min(rate * 100 / (thresholds[2] * 1.5) * 100, 100)}%` }}
        />
      </div>
    </div>
  );
}
